import { useEffect, useState } from "react";
import { Plus, PencilLine, Power, MapPin } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { EmptyState } from "../../components/shared/EmptyState";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { MediaPickerDialog } from "../../components/shared/MediaPickerDialog";
import { api, formatApiError } from "../../lib/api";

const EMPTY = { name: "", city: "", address: "", lat: 50.2967, lng: 3.5389, description: "", capacity: "", website: "", image: "", tags: "", realisation_slugs: [], product_slugs: [], order: 0 };
const toList = (v) => (Array.isArray(v) ? v : (v || "").split(",").map((s) => s.trim()).filter(Boolean));

export default function VenuesAdminPage() {
  const [venues, setVenues] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [slugsText, setSlugsText] = useState({ realisations: "", products: "" });
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = () => api.get("/admin/venues").then((r) => setVenues(r.data)).catch(() => setVenues([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setSlugsText({ realisations: "", products: "" }); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({ name: v.name, city: v.city, address: v.address || "", lat: v.lat, lng: v.lng, description: v.description || "", capacity: v.capacity || "", website: v.website || "", image: v.image || "", tags: (v.tags || []).join(", "), realisation_slugs: v.realisation_slugs || [], product_slugs: v.product_slugs || [], order: v.order || 0 });
    setSlugsText({ realisations: (v.realisation_slugs || []).join(", "), products: (v.product_slugs || []).join(", ") });
    setOpen(true);
  };

  const submit = async () => {
    const payload = {
      ...form,
      lat: Number(form.lat), lng: Number(form.lng), order: Number(form.order) || 0,
      address: form.address || null, description: form.description || null, capacity: form.capacity || null,
      website: form.website || null, image: form.image || null,
      tags: toList(form.tags),
      realisation_slugs: toList(slugsText.realisations), product_slugs: toList(slugsText.products),
    };
    try {
      if (editing) { await api.patch(`/admin/venues/${editing.id}`, payload); toast.success("Lieu mis à jour"); }
      else { await api.post("/admin/venues", payload); toast.success("Lieu créé — visible sur la page /lieux"); }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async (v) => {
    try { await api.post(`/admin/venues/${v.id}/toggle`); toast.success(v.active ? "Lieu retiré du site public" : "Lieu publié sur /lieux"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!venues) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="venues-loading">Chargement…</div>;

  return (
    <div data-testid="admin-venues-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">Lieux de réception</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Lieux de réception</h1>
          <p className="mt-1 text-sm text-muted-foreground">Domaines et salles partenaires géolocalisés, publiés sur la page /lieux avec carte, réalisations et matériel recommandé.</p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid="venue-create">
          <Plus className="h-4 w-4" /> Nouveau lieu
        </button>
      </div>

      {venues.length === 0 ? (
        <div className="mt-6"><EmptyState icon={MapPin} title="Aucun lieu">Ajoutez le premier lieu de réception partenaire.</EmptyState></div>
      ) : (
        <div className="card-ae mt-6 overflow-x-auto">
          <table className="w-full min-w-[680px] text-sm" data-testid="venues-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3">Lieu</th>
                <th className="px-5 py-3">Ville</th>
                <th className="px-5 py-3">Coordonnées</th>
                <th className="px-5 py-3 text-center">Ordre</th>
                <th className="px-5 py-3">Statut</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {venues.map((v) => (
                <tr key={v.id} className="transition-colors hover:bg-cream/60" data-testid={`venue-row-${v.id}`}>
                  <td className="px-5 py-3.5 font-semibold text-deepgreen">{v.name}</td>
                  <td className="px-5 py-3.5 text-muted-foreground">{v.city}</td>
                  <td className="px-5 py-3.5 font-mono text-xs text-muted-foreground">{v.lat?.toFixed(4)}, {v.lng?.toFixed(4)}</td>
                  <td className="px-5 py-3.5 text-center">{v.order}</td>
                  <td className="px-5 py-3.5"><StatusBadge status={v.active ? "Actif" : "Archivé"} /></td>
                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => openEdit(v)} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`venue-edit-${v.id}`}><PencilLine className="h-3.5 w-3.5" /></button>
                      <button type="button" onClick={() => toggle(v)} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`venue-toggle-${v.id}`}>
                        <Power className="mr-1 inline h-3.5 w-3.5" />{v.active ? "Retirer" : "Publier"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-cream sm:max-w-2xl" data-testid="venue-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-deepgreen">{editing ? "Modifier le lieu" : "Nouveau lieu"}</DialogTitle>
            <DialogDescription>Les coordonnées GPS alimentent la carte OpenStreetMap de la page /lieux.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="label-ae" htmlFor="v-name">Nom du lieu</label><input id="v-name" className="input-ae" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="venue-name" /></div>
              <div><label className="label-ae" htmlFor="v-city">Ville</label><input id="v-city" className="input-ae" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="venue-city" /></div>
              <div className="sm:col-span-2"><label className="label-ae" htmlFor="v-address">Adresse</label><input id="v-address" className="input-ae" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="venue-address" /></div>
              <div><label className="label-ae" htmlFor="v-lat">Latitude</label><input id="v-lat" type="number" step="0.0001" className="input-ae" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="venue-lat" /></div>
              <div><label className="label-ae" htmlFor="v-lng">Longitude</label><input id="v-lng" type="number" step="0.0001" className="input-ae" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="venue-lng" /></div>
              <div><label className="label-ae" htmlFor="v-capacity">Capacité (ex : 120 pers.)</label><input id="v-capacity" className="input-ae" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="venue-capacity" /></div>
              <div><label className="label-ae" htmlFor="v-tags">Style de salle (badges, ex : Domaine, Champêtre)</label><input id="v-tags" className="input-ae" placeholder="Domaine, Champêtre…" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="venue-tags" /></div>
              <div><label className="label-ae" htmlFor="v-website">Site web du lieu</label><input id="v-website" type="url" className="input-ae" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="venue-website" /></div>
              <div className="sm:col-span-2"><label className="label-ae" htmlFor="v-desc">Description</label><textarea id="v-desc" className="input-ae min-h-20" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="venue-description" /></div>
              <div className="sm:col-span-2">
                <label className="label-ae" htmlFor="v-image">Photo (Médiathèque)</label>
                <input id="v-image" className="input-ae" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="venue-image" />
                <button type="button" onClick={() => setPickerOpen(true)} className="btn-secondary-ae mt-2 !px-4 !py-1.5 !text-xs" data-testid="venue-pick-media">Choisir depuis la Médiathèque</button>
                {form.image && <img src={form.image} alt="Aperçu du lieu" className="mt-2 h-20 w-auto rounded-lg border border-border object-cover" data-testid="venue-image-preview" />}
              </div>
              <div className="sm:col-span-2">
                <label className="label-ae" htmlFor="v-reals">Réalisations associées (slugs, séparés par des virgules)</label>
                <input id="v-reals" className="input-ae" placeholder="ex : mariage-julie-clement, mariage-amelie-romain" value={slugsText.realisations} onChange={(e) => setSlugsText({ ...slugsText, realisations: e.target.value })} data-testid="venue-realisations" />
              </div>
              <div className="sm:col-span-2">
                <label className="label-ae" htmlFor="v-prods">Matériel recommandé (slugs produits, séparés par des virgules)</label>
                <input id="v-prods" className="input-ae" placeholder="ex : arche-triangulaire-bois, housses-blanches" value={slugsText.products} onChange={(e) => setSlugsText({ ...slugsText, products: e.target.value })} data-testid="venue-products" />
              </div>
              <div><label className="label-ae" htmlFor="v-order">Ordre d'affichage</label><input id="v-order" type="number" className="input-ae" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="venue-order" /></div>
            </div>
            <button type="button" onClick={submit} disabled={form.name.length < 2 || form.city.length < 2} className="btn-primary-ae modal-sticky-btn w-full disabled:opacity-50" data-testid="venue-submit">
              {editing ? "Enregistrer" : "Créer le lieu"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <MediaPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} multiple onSelect={(imgs) => setForm({ ...form, image: imgs.at(-1).url })} />
    </div>
  );
}
